'use client';

import { useState } from 'react';

import Image from 'next/image';

import notFoundIcon from '@/assets/icons/table-dead-state-icon.svg';
import CopyIcon from '@/assets/images/copy-icon-new.svg';
import LinkIcon from '@/assets/images/link-icon.svg';
import HtmlRenderer from '@/components/HtmlRender';
import { useAuthContext } from '@/components/Providers/AuthProvider';
import Button from '@/components/V2/Button';
import TextInput from '@/components/V2/TextInput';
import { useGetImportantLinksQuery } from '@/store/api/dashboardStrapi';

import DeadState from '../DeadState';
import ReportsLoader from '../Reports/CustomerDetailModal/ReportsLoader';

const ImportantLinks = () => {
  const { data, isLoading, isError, isFetching } = useGetImportantLinksQuery();
  const [copiedIndex, setCopiedIndex] = useState<{ section: number; sub: number } | null>(null);
  const { customer } = useAuthContext();

  if (isLoading || !data) {
    return (
      <div className="v2-flex v2-justify-center v2-items-center md:v2-h-[373px] v2-h-[373px] v2-p-8">
        <ReportsLoader />
      </div>
    );
  }

  const handleCopy = (url: string, sectionIndex: number, subIndex: number) => {
    navigator.clipboard.writeText(url).then(() => {
      setCopiedIndex({ section: sectionIndex, sub: subIndex });
      setTimeout(() => setCopiedIndex(null), 2000);
    });
  };

  const { data: pageData, title } = data;

  const { header, sections } = pageData;

  return (
    <div className="v2-shadow-md v2-p-[24px] md:v2-p-[24px] sm:v2-p-4 xs:v2-p-4 v2-bg-white v2-rounded-xs">
      <div className="v2-pb-5 v2-border-b v2-border-[#E5E5E5] v2-mb-5">
        <h2 className="v2-text-[#333333] v2-font-sweet-sans-pro-bold v2-font-bold v2-text-xl v2-text-center">
          {title}
        </h2>
      </div>
      <div className="v2-flex v2-flex-col v2-gap-5">
        {header && (
          <div className="v2-p-3 v2-bg-[#F9FAFB] v2-border v2-border-[#EAECF0] v2-rounded-xs">
            <h3 className="v2-text-primary v2-font-sweet-sans-pro-semi-bold v2-font-semibold v2-text-base v2-mb-3">
              {header?.heading}
            </h3>
            <div className="v2-flex v2-items-center v2-gap-5">
              <div>
                <Image src={header?.logoImage} width={93} height={100} alt="" className="v2-w-[93px]" />
              </div>
              <div>
                <p className="v2-text-[#101828] v2-font-sweet-sans-pro v2-font-normal v2-text-base v2-mb-3">
                  <HtmlRenderer html={JSON.parse(header?.description).value} />
                </p>
                <div className="v2-flex v2-items-center v2-gap-3">
                  <Button
                    btnSize="sm"
                    scheme="text"
                    type="button"
                    variant="outlined"
                    className="!v2-p-0 !v2-bg-transparent v2-text-primary hover:v2-underline v2-flex v2-items-center"
                  >
                    <a href={header?.image1Url} target="_blank" rel="noopener noreferrer">
                      <Image src={header?.image1} width={135} height={40} alt="Google Store Image" />
                    </a>
                  </Button>
                  <Button
                    btnSize="sm"
                    scheme="text"
                    type="button"
                    variant="outlined"
                    className="!v2-p-0 !v2-bg-transparent v2-text-primary hover:v2-underline v2-flex v2-items-center"
                  >
                    <a href={header?.image2Url} target="_blank" rel="noopener noreferrer">
                      <Image src={header?.image2} width={120} height={40} alt="App Store Image" />
                    </a>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
        {sections?.sections.length === 0 && (
          <div className="v2-flex v2-items-center v2-align-middle">
            <DeadState icon={notFoundIcon} title="No Links Found" />
          </div>
        )}
        {sections?.sections?.map((section, sectionIndex) => (
          <>
            <h3 className="v2-text-primary v2-font-sweet-sans-pro-semi-bold v2-font-semibold v2-text-base">
              {section.title}
            </h3>
            {section.subSections.map((subSection, subIndex) => {
              const rawUrl = subSection.url;
              const resolvedUrl = customer?.webAlias
                ? rawUrl?.replace('{0}', customer?.webAlias).replace('%7B0%7D', customer?.webAlias)
                : '';

              return (
                <div key={subIndex}>
                  <label
                    htmlFor=""
                    className="v2-text-[#333333] v2-font-sweet-sans-pro v2-font-normal v2-text-base v2-mb-2 v2-block"
                  >
                    {subSection.title}
                  </label>
                  <div className="v2-relative">
                    <Image src={LinkIcon} alt="" className="v2-absolute v2-left-4 v2-top-[9px] v2-z-[1]" />
                    <TextInput
                      type="text"
                      inputClassName="v2-h-[40px] v2-rounded-xs v2-pl-[43px] v2-pr-[115px] !v2-text-[#4EACD3]"
                      value={resolvedUrl}
                    />
                    <Button
                      btnSize="sm"
                      scheme="primary"
                      type="button"
                      variant="filled"
                      className="v2-absolute v2-right-0 v2-top-[-1px] v2-h-[41px] v2-py-[10px] v2-px-[14px] v2-text-sm v2-sweet-sans-pro-medium v2-font-medium v2-flex v2-items-center v2-justify-center v2-gap-[4px] v2-w-[91px]"
                      onClick={() => handleCopy(resolvedUrl, sectionIndex, subIndex)}
                    >
                      {copiedIndex?.section === sectionIndex && copiedIndex?.sub === subIndex ? 'Copied!' : 'Copy'}
                      {!(copiedIndex?.section === sectionIndex && copiedIndex?.sub === subIndex) && (
                        <Image src={CopyIcon} alt="" />
                      )}
                    </Button>
                  </div>
                </div>
              );
            })}
            <hr className="v2-border-[#E5E5E5]" />
          </>
        ))}
      </div>
    </div>
  );
};

export default ImportantLinks;
